// Mirrors AssetLinks' BetaSecondaryNav usage (../AssetLayout/AssetLinks.tsx),
// scoped to just these two pages since useAssetSidebar() excludes /connections
// routes from the main assets sidebar.
"use client";

import { usePathname } from "next/navigation";
import {
  BetaSecondaryNav,
  BetaSecondaryNavItem,
  BetaSecondaryNavList,
  Tag,
} from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import { useTranslations } from "@zapier/i18n";
import { useSplitTreatments } from "@zapier/client";
import {
  getAppConnectionsIndexUrl,
  getSdkCredentialsIndexUrl,
} from "@zapier/utils";
import styles from "./ConnectionsSdkCredentialsNav.module.css";

export function ConnectionsSdkCredentialsNav() {
  const pathname = usePathname() || "";
  const t = useTranslations("connections-sdk-credentials-nav");
  const { isSdkCredentialsEnabled, isLoaded } = useSplitTreatments();

  // Available only behind sdk-credentials-enabled — while off (or
  // still loading), there is nothing to switch between, so render nothing
  // rather than a one-item sidebar.
  if (!isLoaded || !isSdkCredentialsEnabled) {
    // eslint-disable-next-line unicorn/no-null
    return null;
  }

  const links: {
    href: string;
    icon: "navConnectedAssetsFill" | "actionAIFix";
    text: string;
    badge?: string;
  }[] = [
    {
      href: getAppConnectionsIndexUrl(),
      icon: "navConnectedAssetsFill",
      text: t("labels.connections"),
    },
    {
      href: getSdkCredentialsIndexUrl(),
      icon: "actionAIFix",
      text: t("labels.sdk-credentials"),
      badge: t("labels.beta"),
    },
  ];

  // Check the most specific (longest) href first — App connections' href is
  // now a prefix of SDK credentials' nested href, so a naive first-match
  // would always select App connections.
  const selectedHref = [...links]
    .sort((a, b) => b.href.length - a.href.length)
    .find(
      (link) => pathname === link.href || pathname.startsWith(`${link.href}/`),
    )?.href;

  return (
    <nav className={styles.nav} aria-label={t("aria-label")}>
      <BetaSecondaryNav
        selectedItem={selectedHref}
        aria-label={t("aria-label")}
      >
        <BetaSecondaryNavList>
          {links.map((link) => (
            <BetaSecondaryNavItem
              key={link.href}
              label={
                <>
                  <Icon name={link.icon} size={20} color="GrayWarm6" />
                  {/* design-contract: layout-only wrapper so the label
                      truncates (ellipsis) instead of the badge clipping when
                      the resizable sidebar is dragged narrow. No typography is
                      set here on purpose -- it inherits BetaSecondaryNav's
                      tokenized font (including the selected-state weight),
                      which a <Text> wrapper would override. */}
                  <span className={styles.label}>{link.text}</span>
                  {link.badge && (
                    <Tag size="small" variant="simple" className={styles.badge}>
                      {link.badge}
                    </Tag>
                  )}
                </>
              }
              linkProps={{ href: link.href }}
              itemKey={link.href}
            />
          ))}
        </BetaSecondaryNavList>
      </BetaSecondaryNav>
    </nav>
  );
}
